import { Booking } from '../../core/models/booking';
import { UpcomingSessionsForClientDTO } from '../../infra/adapters/luminara/booking/dto/upcoming-sessions-for-client-dto';
import { extractMessageFromError } from '../../infra/adapters/luminara/common/ErrorUtil';
import { isAccessDenied, isNotFound } from '../../util/auth-util';

export interface ClientDashboardState {
  shouldShowRecommendationReady: boolean;
  firstUpcomingSession?: Booking;
  remainingUpcomingSessions: Booking[];
  cancelledUpcomingSessions?: Booking[];
  showCancelationConfirmModal: boolean;
  showBookingCancelledSuccessModal: boolean;
  bookingToBeCancelled?: Booking;
  dataLoadErrors: string[];
  showLoader: boolean;
  lastFinishedBooking: Booking | null;
}

export const initialClientDashboardState = () => {
  return {
    shouldShowRecommendationReady: false,
    dataLoadErrors: [],
    remainingUpcomingSessions: [],
    showLoader: true,
    showBookingCancelledSuccessModal: false,
    showCancelationConfirmModal: false,
    lastFinishedBooking: null,
  } as ClientDashboardState;
};
export interface ClientDashboardAction {
  type:
    | 'LOAD_UPCOMING_SESSIONS_SUCCESS'
    | 'LOAD_UPCOMING_SESSIONS_FAILURE'
    | 'BOOKING_CANCELLED_BY_CLIENT_SUCCESS'
    | 'BOOKING_CANCELLED_BY_CLIENT_FAILURE'
    | 'SHOW_CANCELATION_CONFIRMATION_MODAL'
    | 'HIDE_CANCELATION_CONFIRMATION_MODAL'
    | 'HIDE_BOOKING_CANCELLED_SUCESSFULLY_MODAL'
    | 'LOAD_LAST_FINISHED_BOOKING_SUCCESS';
  payload?: any;
}

const clientDashboardReducer = (state: ClientDashboardState = initialClientDashboardState(), action: ClientDashboardAction): ClientDashboardState => {
  switch (action.type) {
    case 'LOAD_UPCOMING_SESSIONS_SUCCESS': {
      const payload: UpcomingSessionsForClientDTO = action.payload as UpcomingSessionsForClientDTO;
      const firstUpcomingSession = payload.upcomingBookings.at(0);
      return {
        ...state,
        shouldShowRecommendationReady: payload.shouldShowRecommendationReady,
        firstUpcomingSession: firstUpcomingSession,
        remainingUpcomingSessions: payload.upcomingBookings?.length > 1 ? payload?.upcomingBookings.slice(1) : [],
        cancelledUpcomingSessions: payload.bookingsCancelledByHealer,
        dataLoadErrors: [],
        showLoader: false,
      };
    }
    case 'LOAD_UPCOMING_SESSIONS_FAILURE': {
      if (isAccessDenied(action.payload) || isNotFound(action.payload)) {
        return { ...state, firstUpcomingSession: undefined, remainingUpcomingSessions: [], dataLoadErrors: [], showLoader: false };
      } else {
        return {
          ...state,
          dataLoadErrors: state.dataLoadErrors.concat(extractMessageFromError(action.payload)),
          firstUpcomingSession: undefined,
          remainingUpcomingSessions: [],
          showLoader: false,
          shouldShowRecommendationReady: false,
        };
      }
    }
    case 'BOOKING_CANCELLED_BY_CLIENT_SUCCESS': {
      const bookingId = action.payload;
      if (state.firstUpcomingSession?.id === bookingId) {
        return {
          ...state,
          showBookingCancelledSuccessModal: true,
          showCancelationConfirmModal: false,
          bookingToBeCancelled: undefined,
          firstUpcomingSession: undefined,
        };
      }

      return {
        ...state,
        showBookingCancelledSuccessModal: true,
        showCancelationConfirmModal: false,
        bookingToBeCancelled: undefined,
        remainingUpcomingSessions: state.remainingUpcomingSessions.filter((booking) => booking.id !== bookingId),
      };
    }

    case 'BOOKING_CANCELLED_BY_CLIENT_FAILURE': {
      return {
        ...state,
        dataLoadErrors: state.dataLoadErrors.concat(action.payload),
        showBookingCancelledSuccessModal: false,
        showCancelationConfirmModal: false,
        bookingToBeCancelled: undefined,
      };
    }

    case 'SHOW_CANCELATION_CONFIRMATION_MODAL': {
      const bookingId = action.payload;
      let booking;
      if (state.firstUpcomingSession?.id === bookingId) {
        booking = state.firstUpcomingSession;
      } else {
        booking = state.remainingUpcomingSessions.find((b) => b.id === bookingId);
      }

      return { ...state, showCancelationConfirmModal: true, bookingToBeCancelled: booking };
    }

    case 'HIDE_CANCELATION_CONFIRMATION_MODAL': {
      return {
        ...state,
        showCancelationConfirmModal: false,
        bookingToBeCancelled: undefined,
      };
    }

    case 'HIDE_BOOKING_CANCELLED_SUCESSFULLY_MODAL': {
      return {
        ...state,
        showBookingCancelledSuccessModal: false,
        showCancelationConfirmModal: false,
        bookingToBeCancelled: undefined,
      };
    }

    case 'LOAD_LAST_FINISHED_BOOKING_SUCCESS': {
      return {
        ...state,
        lastFinishedBooking: action.payload,
      };
    }

    default:
      return state;
  }
};

export default clientDashboardReducer;
